import React, { useState } from 'react'
import { CaptionProps, DayPicker as ReactDayPicker } from 'react-day-picker'

import { Box, Caption, Navbar } from '@factorialco/deprecated-design-system'
import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  endOfWeek,
  endOfYear,
  startOfWeek,
  subDays,
  subMonths,
  subYears,
} from 'date-fns'

import { DayOfWeekIndex } from 'modules/core/lib/dates'

import './index.styles.css'

const getBiweekRange = (date: Date): [Date, Date] => {
  return [startOfWeek(date), endOfWeek(addWeeks(date, 1))]
}

type Props = {
  /**
   * The selected day of the week
   */
  date: Date
  onDateRangeChange: (startDate: Date, endDate: Date) => void
  firstDayOfWeek: DayOfWeekIndex
}

export const FortNightPicker = ({ date, onDateRangeChange, firstDayOfWeek }: Props) => {
  const [month, setMonth] = useState(date)
  const [hoverRange, setHoverRange] = useState<[Date, Date]>()

  const selectedRange = React.useMemo(() => getBiweekRange(date), [date])

  // Same logic as in DayPicker
  const minDate = subYears(new Date(), 100)
  const maxDate = endOfYear(addYears(new Date(), 50))

  const handleDayEnter = React.useCallback((hoveredDate: Date) => {
    const range = getBiweekRange(hoveredDate)

    setHoverRange(range)
  }, [])

  const handleDayLeave = React.useCallback(() => {
    setHoverRange(undefined)
  }, [])

  const handleDayClick = React.useCallback(
    (clickedDate: Date) => {
      const range = getBiweekRange(clickedDate)

      onDateRangeChange(range[0], range[1])
    },
    [onDateRangeChange]
  )

  const modifiers = React.useMemo(
    () => ({
      selectedRange: {
        from: addDays(selectedRange[0], 1),
        to: subDays(selectedRange[1], 1),
      },
      selectedRangeStart: selectedRange[0],
      selectedRangeEnd: selectedRange[1],

      ...(hoverRange
        ? {
            hoverRange: {
              from: addDays(hoverRange[0], 1),
              to: subDays(hoverRange[1], 1),
            },
            hoverRangeStart: hoverRange[0],
            hoverRangeEnd: hoverRange[1],
          }
        : {}),
    }),
    [hoverRange, selectedRange]
  )

  const components = React.useMemo(
    () => ({
      Caption: ({ displayMonth }: CaptionProps) => (
        <Box flexDirection="row" justifyContent="spaceBetween" padding="s8">
          <Caption
            onChange={setMonth}
            fromMonth={minDate}
            toMonth={maxDate}
            date={displayMonth}
            hideYear={false}
          />
          <Navbar
            onPreviousClick={() => setMonth(subMonths(displayMonth, 1))}
            onNextClick={() => setMonth(addMonths(displayMonth, 1))}
          />
        </Box>
      ),
    }),
    [minDate, maxDate]
  )

  return (
    <ReactDayPicker
      className={'_FortNightPicker_weekPicker'}
      mode="range"
      modifiers={modifiers}
      modifiersClassNames={{
        isWeekend: 'rdp-day_weekend',
        isHoliday: 'rdp-day_holiday',
        blocked: 'rdp-day_blocked',

        selectedRange: 'rdp-day_range_middle_week rdp-day_selected',
        selectedRangeStart: 'rdp-day_range_start rdp-day_selected',
        selectedRangeEnd: 'rdp-day_range_end rdp-day_selected',

        hoverRange: 'rdp-day_range_hover_middle_week',
        hoverRangeStart: 'rdp-day_range_hover_start_week',
        hoverRangeEnd: 'rdp-day_range_hover_end_week',
      }}
      onDayMouseEnter={handleDayEnter}
      onDayMouseLeave={handleDayLeave}
      onDayClick={handleDayClick}
      fixedWeeks
      showOutsideDays
      month={month}
      components={components}
      weekStartsOn={firstDayOfWeek}
    />
  )
}
